<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper';
import { useRouter } from 'vue-router';
import { ref, onMounted, onUnmounted } from 'vue';
import { checkAndDownloadIntro, getCachedImage } from '@/services/intro';
import KiwishLoader from '@/views/components/KiwishLoader.vue';

const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;
const swiperRef = ref();
const router = useRouter();
const loading = ref(true);
const slides = ref([]);
const cachedImages = ref({});

// Slides por defecto (fallback)
const defaultSlides = [
  { title: 'intro1.title', img: 'intro1.jpg', fontSize: '48px', lineHeight: '50px' },
  { title: 'intro2.title', img: 'intro2.jpg', fontSize: '48px', lineHeight: '50px' },
  { title: 'intro3.title', img: 'intro3.jpg', fontSize: '48px', lineHeight: '50px' },
  { title: 'intro4.title', img: 'intro4.jpg', fontSize: '48px', lineHeight: '50px' },
  { title: 'intro5.title', img: 'intro5.jpg', fontSize: '48px', lineHeight: '50px' },
];

// Función para ajustar la altura del viewport considerando Safari iOS
const setViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

onMounted(async () => {
  // Configurar altura de viewport para Safari iOS
  setViewportHeight();
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', setViewportHeight);

  // Específico para Safari iOS
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', setViewportHeight);
  }

  try {
    // Intentar cargar datos dinámicos
    const introData = await checkAndDownloadIntro();

    if (introData && introData.slides && introData.slides.length > 0) {
      // Merge: usar datos dinámicos con fallback a defaults
      slides.value = introData.slides.map((slide, index) => {
        const defaultSlide = defaultSlides[slide.position - 1] || defaultSlides[index] || defaultSlides[0];

        // Si título es null, "", o " " → mostrar vacío, sino mostrar el título
        const titleValue = (!slide.title || slide.title.trim() === '') ? '' : slide.title;

        return {
          title: titleValue,
          img: slide.image_url !== null && slide.image_url !== undefined ? slide.image_url : defaultSlide.img,
          fontSize: slide.title_font_size || defaultSlide.fontSize,
          lineHeight: slide.title_line_height || defaultSlide.lineHeight,
          isDynamic: slide.image_url !== null && slide.image_url !== undefined,
          isTitleDynamic: true
        };
      });
      cachedImages.value = introData.images || {};
    } else {
      // Usar datos por defecto
      slides.value = defaultSlides.map(s => ({ ...s, isDynamic: false, isTitleDynamic: false }));
    }
  } catch (error) {
    // En caso de error, usar slides por defecto
    slides.value = defaultSlides.map(s => ({ ...s, isDynamic: false, isTitleDynamic: false }));
  } finally {
    loading.value = false;
  }
});

const toRegister = () => {
    router.push({path: '/auth/register'});
}

const getSlideImage = (slide) => {
  if (slide.isDynamic) {
    return getCachedImage(slide.img, cachedImages.value);
  }
  return VUE_ASSETS_URL + 'imgs/intro/' + slide.img;
};

let touchStartX = 0;
let touchStartIndex = -1;

const handleTouchStart = (e) => {
  touchStartX = e.touches[0].clientX;
  const swiper = swiperRef.value?.$el?.swiper;
  if (swiper) {
    // Guardamos en qué slide EMPEZÓ el touch
    touchStartIndex = swiper.activeIndex;
  }
};

const handleTouchEnd = (e) => {
  // Solo actuar si el touch EMPEZÓ en la última slide
  if (touchStartIndex === slides.value.length - 1) {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;

    // Si hizo swipe a la izquierda (intento de avanzar) más de 50px
    if (diff > 50) {
      toRegister();
    }
  }
  touchStartIndex = -1;
};

const handleSlideClick = (index) => {
  if (index < slides.value.length - 1) {
    swiperRef.value?.$el?.swiper?.slideNext();
  } else {
    toRegister();
  }
};

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('resize', setViewportHeight);
  window.removeEventListener('orientationchange', setViewportHeight);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', setViewportHeight);
  }
});
</script>


<template>
  <ion-page>
    <KiwishLoader v-if="loading" :is-open="loading" :message="$t('loading')" />

    <ion-content v-else :fullscreen="true" class="ion-padding content-bg" v-bind="$attrs">
      <swiper
        ref="swiperRef"
        :modules="[Navigation, Pagination]"
        class="mySwiper"
        :resistance="true"
        :resistanceRatio="0"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
      >
        <swiper-slide v-for="(slide, index) in slides" :key="index" >
          <div
            class="slide-wrapper"
            @click="handleSlideClick(index)"
          >
            <img class="slide-bg" :src="getSlideImage(slide)" />
            <div class="slide-overlay"></div>

            <div class="slide-content">
              <div class="slide-text">
                <ion-text
                  class="slide-title"
                  :style="{ fontSize: slide.fontSize, lineHeight: slide.lineHeight }"
                >{{ slide.isTitleDynamic ? slide.title : $t(slide.title) }}</ion-text>
              </div>
            </div>

            <div class="points">
              <div
                v-for="(_, n) in slides"
                :key="n"
                :class="n === index ? 'point-selected' : 'point'"
                @click.stop="swiperRef?.$el?.swiper?.slideTo(n)"
              ></div>
            </div>
          </div>
          <!-- <img class="loader" :class="'rotate'+index" :src="VUE_ASSETS_URL + 'imgs/icons/load_kiwish.png'" /> -->
        </swiper-slide>
      </swiper>
    </ion-content>
  </ion-page>
</template>
  
<style scoped>
  /* ion-header ion-toolbar {
    --background: var(--ion-color-primary, #121212) !important;
    background: var(--ion-color-primary, #121212) !important;
  } */
  ion-header ion-toolbar .ios {padding-top: 30px;}

  ion-content {
    --offset-bottom: 0;
    --padding-bottom: 0;
  }
  .mySwiper {
    position:fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100vh; /* Fallback */
    height: calc(var(--vh, 1vh) * 100); /* Para Safari iOS */
    height: 100dvh; /* Para navegadores modernos */
    background-color: #000;
  }
  .box{position:fixed; bottom: 0;right:15px;left:15px;}
  .logo {
    display: flex; justify-content: center; align-items: center;
  }  

.slide-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.slide-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.25);
}

.slide-content {
  position: absolute;
  bottom: calc(90px + env(safe-area-inset-bottom, 0px));
  left: 25px;
  right: 25px;
}

.slide-title {
  font-size: 48px;
  line-height: 50px;
  font-weight: 600;
  color: #F8E9BF;
  width: 80%;
  display: block;
}

.points {
  display: flex;
  position: absolute;
  bottom: calc(5% + env(safe-area-inset-bottom, 0px));
  left: 25px;
  right: 25px;
}

.point {
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background-color: #FFF7E2;
  margin: 0 5px;
}

.point-selected {
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background-color: #6DA471;
  margin: 0 5px;
}
.loader {
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  position: absolute;
  bottom: calc(3% + env(safe-area-inset-bottom, 0px));
  right: 9%;
}
.rotate0 {
  transform: rotate(0deg);
}
.rotate1 {
  transform: rotate(90deg);
}
.rotate2 {
  transform: rotate(180deg);
}
.rotate3 {
  transform: rotate(270deg);
}

</style>